import {
	ChangeDetectionStrategy,
	Component,
	inject,
	signal,
} from '@angular/core';
import {
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MessagesService } from '@pressteaze/ui';
import { AuthApiService } from './auth-api.service';
import { isStoreChallenge, type OperatorStoreChallenge } from './auth.models';

@Component({
	selector: 'lib-operator-login',
	imports: [ReactiveFormsModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './operator-login.component.html',
	styleUrl: './operator-login.component.css',
})
export class OperatorLoginComponent {
	private readonly api = inject(AuthApiService);
	private readonly router = inject(Router);
	private readonly messagesSrv = inject(MessagesService);
	readonly busy = signal(false);
	readonly challenge = signal<OperatorStoreChallenge | null>(null);
	readonly pinForm = new FormGroup({
		pin: new FormControl('', {
			nonNullable: true,
			validators: [Validators.required, Validators.pattern(/^\d{4,8}$/)],
		}),
	});
	readonly storeForm = new FormGroup({
		storeId: new FormControl('', {
			nonNullable: true,
			validators: [Validators.required],
		}),
	});

	async login(): Promise<void> {
		if (this.pinForm.invalid || this.busy()) return;
		this.busy.set(true);
		try {
			const response = await this.api.operatorLogin(
				this.pinForm.controls.pin.value,
			);
			if (isStoreChallenge(response)) {
				this.challenge.set(response);
				this.storeForm.controls.storeId.setValue(
					response.default_store_id ?? response.stores[0]?.id ?? '',
				);
			} else {
				this.messagesSrv.success('Συνδεθήκατε επιτυχώς');
				await this.router.navigateByUrl('/');
			}
		} catch {
			this.messagesSrv.error(
				'Η σύνδεση απέτυχε',
				'Το PIN δεν είναι σωστό ή η πρόσβαση έχει κλειδωθεί προσωρινά.',
			);
		} finally {
			this.busy.set(false);
		}
	}

	async continue(): Promise<void> {
		const challenge = this.challenge();
		if (!challenge || this.storeForm.invalid || this.busy()) return;
		this.busy.set(true);
		try {
			await this.api.selectStore(
				challenge.login_challenge,
				this.storeForm.controls.storeId.value,
			);
			this.messagesSrv.success('Το κατάστημα επιλέχθηκε');
			await this.router.navigateByUrl('/');
		} catch {
			this.messagesSrv.error(
				'Δεν ολοκληρώθηκε η σύνδεση',
				'Δεν ήταν δυνατή η επιλογή καταστήματος. Δοκιμάστε ξανά.',
			);
		} finally {
			this.busy.set(false);
		}
	}
}
